import React, { useState } from 'react';

const Services = () => {
    // Default active service
    const [active, setActive] = useState(0);

    const services = [
        {
            id: 0,
            title: "Custom SaaS Engines",
            description: "We don't use templates. We engineer custom web applications that scale.",
            highlight: "MERN Stack • Microservices",
            // This is the 'Graphic' for this service (Concept: A Code/Browser Window)
            visualColor: "from-neon-blue to-blue-600",
            icon: "⚡️"
        },
        {
            id: 1,
            title: "E-Commerce Ecosystems",
            description: "Secure payment flows, inventory management, and high-conversion UI design.",
            highlight: "Stripe • Inventory Sync",
            // Graphic color for shop
            visualColor: "from-purple-500 to-pink-600",
            icon: "💳"
        },
        {
            id: 2,
            title: "Mobile Architecture",
            description: "React Native solutions that feel native on iOS and Android.",
            highlight: "Cross-Platform • High FPS",
            // Graphic color for mobile
            visualColor: "from-green-400 to-emerald-600",
            icon: "📱"
        }
    ];

    return (
        <section className="bg-void py-24 relative overflow-hidden">
            
            {/* Background Gradient Mesh */}
            <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-gray-800/20 via-void to-void pointer-events-none" />

            <div className="container mx-auto px-6">
                
                <div className="mb-16">
                    <h2 className="text-4xl md:text-6xl font-black text-white mb-4 font-display">
                        SYSTEM <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-blue to-purple-500">CAPABILITIES</span>
                    </h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

                    {/* LEFT COLUMN: Interactive List */}
                    <div className="flex flex-col gap-6">
                        {services.map((service, index) => (
                            <div 
                                key={service.id}
                                onMouseEnter={() => setActive(index)}
                                className={`p-8 rounded-2xl cursor-pointer transition-all duration-500 border border-transparent ${
                                    active === index 
                                    ? 'bg-surface border-white/10 shadow-[0_0_30px_rgba(0,0,0,0.5)] translate-x-4' 
                                    : 'hover:bg-white/5 opacity-50 hover:opacity-100'
                                }`}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className={`text-2xl font-bold ${active === index ? 'text-white' : 'text-gray-400'}`}>
                                        {service.title}
                                    </h3>
                                    <span className="text-2xl">{service.icon}</span>
                                </div>
                                
                                <p className="text-gray-400 mb-6 leading-relaxed">
                                    {service.description}
                                </p>

                                {/* Badge */}
                                <div className={`inline-block px-3 py-1 rounded-full text-xs font-mono font-bold uppercase tracking-wider ${
                                    active === index ? 'bg-neon-blue/10 text-neon-blue' : 'bg-gray-800 text-gray-500'
                                }`}>
                                    {service.highlight}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* RIGHT COLUMN: The Graphic Display (Sticky) */}
                    {/* This stays fixed while you look at the list */}
                    <div className="hidden lg:block sticky top-24 h-[500px]">
                        <div className="relative w-full h-full bg-surface rounded-3xl border border-white/10 overflow-hidden shadow-2xl p-8 flex items-center justify-center">
                            
                            {/* 1. The Glowing Blob (Changes Color) */}
                            <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-gradient-to-r ${services[active].visualColor} blur-[100px] opacity-40 transition-all duration-1000`} />

                            {/* 2. The Abstract Graphic (CSS Composition) */}
                            {/* This simulates a "Screen" or "App Interface" */}
                            <div className="relative z-10 w-full max-w-sm aspect-square">
                                
                                {/* Main Window */}
                                <div className="absolute inset-0 bg-black/80 backdrop-blur-xl rounded-xl border border-white/10 p-4 transform transition-transform duration-700 hover:rotate-2 hover:scale-105">
                                    {/* Fake Header */}
                                    <div className="flex gap-2 mb-4 border-b border-white/10 pb-4">
                                        <div className="w-3 h-3 rounded-full bg-red-500/50" />
                                        <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
                                        <div className="w-3 h-3 rounded-full bg-green-500/50" />
                                    </div>
                                    
                                    {/* Fake Content Skeleton */}
                                    <div className="space-y-3 animate-pulse">
                                        <div className="h-20 bg-white/5 rounded-lg w-full" />
                                        <div className="flex gap-3">
                                            <div className="h-32 bg-white/5 rounded-lg w-1/3" />
                                            <div className="h-32 bg-white/5 rounded-lg w-2/3" />
                                        </div>
                                        <div className="h-4 bg-white/5 rounded w-3/4" />
                                        <div className="h-4 bg-white/5 rounded w-1/2" />
                                    </div>

                                    {/* Overlay Text representing the Service */}
                                    <div className="absolute bottom-8 left-8 right-8">
                                        <div className="text-5xl font-black text-white/10 uppercase font-display">
                                            {services[active].title.split(" ")[0]}
                                        </div>
                                    </div>
                                </div>

                                {/* Floating Elements behind */}
                                <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/5 rounded-lg border border-white/10 backdrop-blur-md animate-bounce delay-700" />
                                <div className="absolute -bottom-6 -left-6 w-32 h-16 bg-white/5 rounded-lg border border-white/10 backdrop-blur-md animate-pulse" />

                            </div>

                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default Services;